import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, Link2, Image as ImageIcon, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Type, Palette, Eraser,
} from 'lucide-react';
import './richEditor.css';

// RichEditor — a real, self-contained rich-text editor.
//
// contentEditable surface + an inline toolbar driven by document.execCommand.
// Body is HTML. Used by both CreateScreen and TemplatesScreen so a user can
// DRAFT FREELY (bold/italic/underline, font colour, font size, alignment,
// bullet/numbered lists, links, inline images) without picking a template.
//
// Image insert embeds the picked file as an inline data: URL for now — a real
// upload to a server is labelled a "next build wave" via the menu hint, but the
// data-URL path is fully functional so a draft is never a dead end.
//
// We deliberately use execCommand: it's deprecated but universally supported,
// dependency-free, and exactly right for a contained admin composer. No global
// CSS is touched — all styles live in the co-located richEditor.css.

const FONT_SIZES = [
  { label: 'Small', value: '2' },
  { label: 'Normal', value: '3' },
  { label: 'Large', value: '5' },
  { label: 'Huge', value: '6' },
];

// Restrained, on-brand swatches (gold + neutrals) plus a few accents.
const SWATCHES = [
  '#1c1c20', '#5a5a64', '#9A8341', '#C6A75A', '#E6C579',
  '#1f6f50', '#2563a8', '#9a2f2f', '#5b3f8c', '#ffffff',
];

function ToolbarButton({ title, onMouseDown, active, children, wide }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`re-tb ${active ? 're-on' : ''} ${wide ? 're-wide' : ''}`}
      // onMouseDown (not onClick) so the editor keeps its selection/focus.
      onMouseDown={(e) => { e.preventDefault(); onMouseDown(e); }}
    >
      {children}
    </button>
  );
}

export default function RichEditor({
  value = '',
  onChange,
  placeholder = 'Start typing your message…',
  minHeight = 220,
  toolbarExtras = null, // e.g. merge-tag / RSVP-link buttons injected by the host
  headerFooter = false, // show header / footer / margin controls
}) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const savedRange = useRef(null);
  const lastEmitted = useRef(value);

  const [colorOpen, setColorOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);

  // Document chrome (header text, footer text, body margin) — optional.
  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('');
  const [margin, setMargin] = useState(20);
  const [chromeOpen, setChromeOpen] = useState(false);

  // Keep the DOM in sync when the value is changed from OUTSIDE (e.g. Milo
  // draft, template load) — but never stomp the caret while the user types
  // (those edits already match lastEmitted).
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastEmitted.current) {
      ref.current.innerHTML = value || '';
      lastEmitted.current = value || '';
    }
  }, [value]);

  // Initial paint.
  useEffect(() => {
    if (ref.current && !ref.current.innerHTML) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastEmitted.current = html;
    onChange && onChange(html);
  }, [onChange]);

  const saveSelection = () => {
    const sel = window.getSelection && window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };
  const restoreSelection = () => {
    const sel = window.getSelection && window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  // Run an execCommand against the current selection, then emit + refocus.
  const exec = (command, arg = null) => {
    if (!ref.current) return;
    ref.current.focus();
    restoreSelection();
    try {
      document.execCommand('styleWithCSS', false, command === 'foreColor');
      document.execCommand(command, false, arg);
    } catch { /* noop — unsupported command degrades gracefully */ }
    emit();
    saveSelection();
  };

  const onInput = () => emit();

  const onPaste = (e) => {
    // Paste as plain text so foreign styles don't pollute the document.
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
    emit();
  };

  const addLink = () => {
    saveSelection();
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL (https://…)', 'https://');
    if (!url) return;
    const safe = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    exec('createLink', safe);
    // Make new links open in a new tab where possible.
    try {
      const sel = window.getSelection();
      const node = sel && sel.anchorNode;
      const a = node && (node.nodeType === 1 ? node.closest('a') : node.parentElement && node.parentElement.closest('a'));
      if (a) { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer'); }
    } catch { /* noop */ }
    emit();
  };

  const onPickImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      restoreSelection();
      // Inline data: URL so the image is fully embedded in the HTML body.
      const html = `<img src="${reader.result}" alt="${(file.name || 'image').replace(/"/g, '')}" style="max-width:100%;height:auto;border-radius:8px;margin:6px 0;" />`;
      try { document.execCommand('insertHTML', false, html); } catch { /* noop */ }
      emit();
    };
    reader.readAsDataURL(file);
  };

  const clearFormatting = () => {
    exec('removeFormat');
    exec('unlink');
  };

  // Close popovers on any outside click.
  useEffect(() => {
    if (!colorOpen && !sizeOpen) return undefined;
    const close = () => { setColorOpen(false); setSizeOpen(false); };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [colorOpen, sizeOpen]);

  const bodyStyle = {
    minHeight,
    padding: `${Math.max(8, Math.min(64, margin))}px`,
  };

  return (
    <div className="re-wrap">
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickImage} />
      <div className="re-bar" onMouseDown={saveSelection}>
        <ToolbarButton title="Bold" onMouseDown={() => exec('bold')}><Bold size={13} /></ToolbarButton>
        <ToolbarButton title="Italic" onMouseDown={() => exec('italic')}><Italic size={13} /></ToolbarButton>
        <ToolbarButton title="Underline" onMouseDown={() => exec('underline')}><Underline size={13} /></ToolbarButton>

        <span className="re-div" />

        {/* Font size */}
        <div className="re-pop-host" onClick={(e) => e.stopPropagation()}>
          <ToolbarButton title="Font size" onMouseDown={() => { saveSelection(); setSizeOpen((o) => !o); setColorOpen(false); }}>
            <Type size={13} />
          </ToolbarButton>
          {sizeOpen && (
            <div className="re-pop">
              {FONT_SIZES.map((f) => (
                <button key={f.value} type="button" className="re-pop-row"
                  onMouseDown={(e) => { e.preventDefault(); exec('fontSize', f.value); setSizeOpen(false); }}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Font colour */}
        <div className="re-pop-host" onClick={(e) => e.stopPropagation()}>
          <ToolbarButton title="Font color" onMouseDown={() => { saveSelection(); setColorOpen((o) => !o); setSizeOpen(false); }}>
            <Palette size={13} />
          </ToolbarButton>
          {colorOpen && (
            <div className="re-pop re-swatches" onMouseDown={(e) => e.preventDefault()}>
              {SWATCHES.map((c) => (
                <button key={c} type="button" className="re-swatch" style={{ background: c }} title={c}
                  onMouseDown={(e) => { e.preventDefault(); exec('foreColor', c); setColorOpen(false); }} />
              ))}
              <label className="re-swatch-custom" title="Custom color">
                <input type="color" onChange={(e) => { exec('foreColor', e.target.value); setColorOpen(false); }} />
              </label>
            </div>
          )}
        </div>

        <span className="re-div" />

        <ToolbarButton title="Align left" onMouseDown={() => exec('justifyLeft')}><AlignLeft size={13} /></ToolbarButton>
        <ToolbarButton title="Align center" onMouseDown={() => exec('justifyCenter')}><AlignCenter size={13} /></ToolbarButton>
        <ToolbarButton title="Align right" onMouseDown={() => exec('justifyRight')}><AlignRight size={13} /></ToolbarButton>

        <span className="re-div" />

        <ToolbarButton title="Bullet list" onMouseDown={() => exec('insertUnorderedList')}><List size={13} /></ToolbarButton>
        <ToolbarButton title="Numbered list" onMouseDown={() => exec('insertOrderedList')}><ListOrdered size={13} /></ToolbarButton>

        <span className="re-div" />

        <ToolbarButton title="Insert link" onMouseDown={addLink}><Link2 size={13} /></ToolbarButton>
        <ToolbarButton title="Insert image (embedded inline — server upload is a next build wave)" onMouseDown={() => { saveSelection(); fileRef.current && fileRef.current.click(); }}><ImageIcon size={13} /></ToolbarButton>
        <ToolbarButton title="Clear formatting" onMouseDown={clearFormatting}><Eraser size={13} /></ToolbarButton>

        {headerFooter && (
          <>
            <span className="re-div" />
            <ToolbarButton title="Header, footer & margins" active={chromeOpen} wide
              onMouseDown={() => setChromeOpen((o) => !o)}>
              Layout
            </ToolbarButton>
          </>
        )}

        {toolbarExtras && (
          <>
            <span className="re-div" />
            {toolbarExtras}
          </>
        )}
      </div>

      {headerFooter && chromeOpen && (
        <div className="re-chrome" onMouseDown={(e) => e.stopPropagation()}>
          <div className="re-chrome-row">
            <label className="re-chrome-lab">Header text
              <input className="re-chrome-inp" value={header} onChange={(e) => setHeader(e.target.value)} placeholder="e.g. You're Invited" />
            </label>
            <label className="re-chrome-lab">Footer text
              <input className="re-chrome-inp" value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="e.g. Thank you for celebrating with us" />
            </label>
            <label className="re-chrome-lab re-chrome-margin">Body margin
              <input type="range" min="8" max="56" value={margin} onChange={(e) => setMargin(Number(e.target.value))} />
              <span className="re-chrome-val">{margin}px</span>
            </label>
          </div>
          <div className="re-chrome-hint">Header / footer wrap the body in the preview &amp; saved HTML. Margins adjust the body inset.</div>
        </div>
      )}

      <div className="re-doc">
        {headerFooter && header ? <div className="re-doc-header">{header}</div> : null}
        <div
          ref={ref}
          className="re-edit"
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          style={bodyStyle}
          onInput={onInput}
          onPaste={onPaste}
          onBlur={() => { saveSelection(); emit(); }}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
        />
        {headerFooter && footer ? <div className="re-doc-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

// (RichEditor.composeHtml static helper removed — it had no call sites; the
// header/footer chrome is rendered inline in the editor.)
